import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { PageHeading, NtIcon } from '../components/NotionUI';
import { getTicketProjects, updateTicketProject, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';

// Tenere allineato a NotificationTemplate::DEFAULTS (backend) - il testo che
// vede l'admin per un progetto senza template personalizzato deve combaciare
// con quello che verrebbe usato lato server.
const DEFAULT_NOTIFY_TEMPLATES = {
  create: {
    subject: 'Ticket #{{ticket_id}} ricevuto - {{project_name}}',
    body: 'Ciao {{customer_name}},\n\nAbbiamo ricevuto la tua richiesta "{{subject}}".\nPuoi seguirne lo stato e rispondere qui:\n{{track_url}}\n\nSe in futuro vuoi accedere a tutti i tuoi ticket, puoi registrarti con questa stessa email.',
  },
  reply: {
    subject: 'Nuova risposta al tuo ticket #{{ticket_id}}',
    body: 'C\'è una nuova risposta al tuo ticket "{{subject}}".\n\nVedi qui: {{track_url}}',
  },
  close: {
    subject: 'Il tuo ticket #{{ticket_id}} è stato chiuso',
    body: 'Il tuo ticket "{{subject}}" è stato chiuso.\n\nPuoi rivederlo qui:\n{{track_url}}',
  },
};

const NOTIFY_TYPES = ['create', 'reply', 'close'];

// Placeholder disponibili nei template email (App\Services\NotificationTemplate::PLACEHOLDERS lato backend).
const NOTIFY_PLACEHOLDERS = ['ticket_id', 'project_code', 'project_name', 'customer_name', 'subject', 'track_url'];

// Dati d'esempio per l'anteprima - project_code/project_name arrivano dal
// progetto vero (vedi previewVars), il resto è fisso: mostra come apparirà
// l'email senza dover creare un ticket reale.
const PREVIEW_SAMPLE = {
  ticket_id: '1234',
  customer_name: 'Mario Rossi',
  subject: 'Non riesco ad accedere al portale',
  track_url: 'https://tuosito.it/portal/ticket/xxxxxxxx-xxxx',
};

function renderTemplate(template, vars) {
  let result = template || '';
  NOTIFY_PLACEHOLDERS.forEach((key) => {
    result = result.split(`{{${key}}}`).join(vars[key] ?? '');
  });
  return result;
}

const TYPE_ICON = { create: 'ticket', reply: 'mail', close: 'status' };
const TYPE_COLOR = { create: '#2563eb', reply: '#7c3aed', close: '#16a34a' };

/** Un blocco "attiva + oggetto + testo + anteprima" per un tipo di notifica (create/reply/close). */
function NotifyTemplateBlock({ type, labelKey, form, setForm, t, previewVars, registerFieldRef, onFieldFocus }) {
  const onKey = `notify_on_${type}`;
  const subjectKey = `notify_${type}_subject`;
  const bodyKey = `notify_${type}_body`;
  const enabled = form[onKey];

  const resetToDefault = () => {
    setForm({
      ...form,
      [subjectKey]: DEFAULT_NOTIFY_TEMPLATES[type].subject,
      [bodyKey]: DEFAULT_NOTIFY_TEMPLATES[type].body,
    });
  };

  return (
    <section className={'nf-block' + (enabled ? '' : ' nf-block-off')} style={{ '--q': TYPE_COLOR[type] }}>
      <header className="nf-head">
        <span className="au-event-icon" aria-hidden="true"><NtIcon name={TYPE_ICON[type]} size={16} /></span>
        <h3>{t(labelKey)}</h3>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          className={'au-switch' + (enabled ? ' au-switch-on' : '')}
          onClick={() => setForm({ ...form, [onKey]: !enabled })}
          aria-label={t(labelKey)}
        >
          <span className="au-switch-knob" />
        </button>
      </header>
      <div className="nf-cols">
        <div className="nf-editor">
          <div className="form-group">
            <label>{t('tickets.notifySubjectLabel')}</label>
            <input
              ref={(node) => registerFieldRef(subjectKey, node)}
              value={form[subjectKey]}
              onFocus={() => onFieldFocus(subjectKey)}
              onChange={(e) => setForm({ ...form, [subjectKey]: e.target.value })}
              disabled={!enabled}
            />
          </div>
          <div className="form-group">
            <label>{t('tickets.notifyBodyLabel')}</label>
            <textarea
              ref={(node) => registerFieldRef(bodyKey, node)}
              rows={7}
              value={form[bodyKey]}
              onFocus={() => onFieldFocus(bodyKey)}
              onChange={(e) => setForm({ ...form, [bodyKey]: e.target.value })}
              disabled={!enabled}
            />
          </div>
          <button type="button" className="nt-ghost-btn nf-reset" onClick={resetToDefault} disabled={!enabled}>
            ↺ {t('tickets.notifyResetDefault')}
          </button>
        </div>
        <div className="nf-preview">
          <div className="nf-preview-label">{t('tickets.notifyPreviewLabel')}</div>
          <div className="nf-mail">
            <div className="nf-mail-meta">
              <span>{t('tickets.notifyFrom')}</span><strong>{previewVars.project_name}</strong>
              <span>{t('tickets.notifyTo')}</span><strong>{previewVars.customer_name}</strong>
            </div>
            <div className="nf-mail-subject">{renderTemplate(form[subjectKey], previewVars)}</div>
            <div className="nf-mail-body">{renderTemplate(form[bodyKey], previewVars)}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Notifiche email al cliente di UNA coda di assistenza, in una schermata a
 * parte raggiungibile da "Code di assistenza" (stesso principio di "Modulo"
 * per i campi del form) invece che infilate nel form principale del
 * progetto, ormai troppo lungo con 3 template completi al suo interno.
 */
function TicketProjectNotifications({ user, onLogout }) {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const projectId = Number(id);

  const [project, setProject] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Un solo elenco di placeholder in cima alla pagina (non ripetuto in ogni
  // blocco): il click inserisce il placeholder nell'ultimo campo oggetto/testo
  // su cui l'admin ha cliccato, tra i 3 blocchi sottostanti.
  const [activeFieldKey, setActiveFieldKey] = useState('notify_create_body');
  const fieldRefs = useRef({});
  const registerFieldRef = useCallback((key, node) => {
    fieldRefs.current[key] = node;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getTicketProjects();
      const p = (data.projects || []).find((proj) => proj.id === projectId);
      if (!p) {
        setError(t('tickets.projectNotFound'));
        setProject(null);
        setForm(null);
        return;
      }
      setProject(p);
      setForm({
        notify_on_create: p.notify_on_create !== false,
        notify_create_subject: p.notify_create_subject || DEFAULT_NOTIFY_TEMPLATES.create.subject,
        notify_create_body: p.notify_create_body || DEFAULT_NOTIFY_TEMPLATES.create.body,
        notify_on_reply: p.notify_on_reply !== false,
        notify_reply_subject: p.notify_reply_subject || DEFAULT_NOTIFY_TEMPLATES.reply.subject,
        notify_reply_body: p.notify_reply_body || DEFAULT_NOTIFY_TEMPLATES.reply.body,
        notify_on_close: p.notify_on_close !== false,
        notify_close_subject: p.notify_close_subject || DEFAULT_NOTIFY_TEMPLATES.close.subject,
        notify_close_body: p.notify_close_body || DEFAULT_NOTIFY_TEMPLATES.close.body,
        csat_enabled: !!p.csat_enabled,
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const insertPlaceholder = (placeholder) => {
    const key = activeFieldKey;
    const el = fieldRefs.current[key];
    const current = form[key] || '';
    const start = el ? el.selectionStart ?? current.length : current.length;
    const end = el ? el.selectionEnd ?? current.length : current.length;
    const newValue = current.slice(0, start) + placeholder + current.slice(end);
    setForm((f) => ({ ...f, [key]: newValue }));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + placeholder.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await updateTicketProject(projectId, form);
      navigate('/ticket-projects');
    } catch (err) {
      setError(errorMessage(err, t('tickets.projectSaveError')));
    } finally {
      setSaving(false);
    }
  };

  const previewVars = {
    ...PREVIEW_SAMPLE,
    project_code: project?.code || 'DEMO',
    project_name: project?.name || t('tickets.projectName'),
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
      <PageHeading icon="bell" title={t('tickets.manageNotifications')} subtitle={project ? project.name : ''} />

      <div className="tp-toolbar">
        <button className="tpl-back fd-back" type="button" onClick={() => navigate('/ticket-projects')}>
          <NtIcon name="back" /> {t('tickets.backToQueues')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading || !form ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <form onSubmit={handleSubmit} className="nf-form">
          <p className="tp-hint nf-hint">{t('tickets.notifyHint')}</p>

          <div className="nf-legend">
            <div className="nf-legend-title">{t('tickets.notifyPlaceholdersTitle')}</div>
            <div className="nf-legend-list">
              {NOTIFY_PLACEHOLDERS.map((key) => (
                <button
                  key={key}
                  type="button"
                  className="nf-chip"
                  title={t(`tickets.placeholder_${key}`)}
                  onClick={() => insertPlaceholder(`{{${key}}}`)}
                >
                  <code>{`{{${key}}}`}</code>
                  <span>{t(`tickets.placeholder_${key}`)}</span>
                </button>
              ))}
            </div>
          </div>

          {NOTIFY_TYPES.map((type) => (
            <NotifyTemplateBlock
              key={type}
              type={type}
              labelKey={`tickets.notifyOn_${type}`}
              form={form}
              setForm={setForm}
              t={t}
              previewVars={previewVars}
              registerFieldRef={registerFieldRef}
              onFieldFocus={setActiveFieldKey}
            />
          ))}

          <section className="nf-block nf-csat">
            <label className="rp-check">
              <input type="checkbox" checked={!!form.csat_enabled} onChange={(e) => setForm({ ...form, csat_enabled: e.target.checked })} />
              <strong>{t('tickets.csatToggle')}</strong>
            </label>
            <p className="tp-hint">{t('tickets.csatHint')}</p>
          </section>

          <div className="tp-savebar">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/ticket-projects')}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      )}
      </div>
    </Layout>
  );
}

export default TicketProjectNotifications;
